import type { CRMUser } from '../types';
import { normalizeCrmUserDisplayRole } from './userRoles';

export type TeamUserListGroupId =
  | 'admin'
  | 'employee'
  | 'employee_shop'
  | 'employee_office'
  | '1099'
  | 'staff'
  | 'designer'
  | 'architect'
  | 'engineer'
  | 'supplier'
  | 'subcontractor'
  | 'cpa'
  | 'client';

export const TEAM_USER_LIST_GROUP_ORDER: TeamUserListGroupId[] = [
  'admin',
  'employee',
  'employee_shop',
  'employee_office',
  '1099',
  'staff',
  'designer',
  'architect',
  'engineer',
  'supplier',
  'subcontractor',
  'cpa',
  'client',
];

const GROUP_LABELS: Record<TeamUserListGroupId, string> = {
  admin: 'Admins',
  employee: 'Employee — Full (W-2)',
  employee_shop: 'Employee — Shop (W-2)',
  employee_office: 'Employee — Office (W-2)',
  '1099': '1099 contractors',
  staff: 'Field staff',
  designer: 'Designers',
  architect: 'Architects',
  engineer: 'Engineers',
  supplier: 'Suppliers',
  subcontractor: 'Trades',
  cpa: 'CPAs',
  client: 'Clients (homeowner logins — not 1099)',
};

export function teamUserListGroupKey(
  user: Pick<CRMUser, 'role' | 'supplierOrgId' | 'is1099Contractor'>,
): TeamUserListGroupId {
  const role = normalizeCrmUserDisplayRole(user);
  if (role === 'employee_shop') return 'employee_shop';
  if (role === 'employee_office') return 'employee_office';
  if (role === 'employee') return 'employee';
  if (user.is1099Contractor === true) {
    if (role === 'designer' || role === 'architect' || role === 'engineer') {
      return role as TeamUserListGroupId;
    }
    return '1099';
  }
  if (role === 'subcontractor') return 'subcontractor';
  return role as TeamUserListGroupId;
}

export function teamUserListGroupLabel(groupId: TeamUserListGroupId): string {
  return GROUP_LABELS[groupId] || groupId;
}

export type TeamUserListGroup<T extends CRMUser = CRMUser> = {
  id: TeamUserListGroupId;
  label: string;
  users: T[];
};

/** Preserve input order within each group; omit empty groups. */
export function groupTeamUsers<T extends CRMUser>(users: T[]): TeamUserListGroup<T>[] {
  const buckets = new Map<TeamUserListGroupId, T[]>();
  for (const u of users) {
    const id = teamUserListGroupKey(u);
    const list = buckets.get(id);
    if (list) list.push(u);
    else buckets.set(id, [u]);
  }
  return TEAM_USER_LIST_GROUP_ORDER.filter((id) => {
    const list = buckets.get(id);
    return list && list.length > 0;
  }).map((id) => ({
    id,
    label: teamUserListGroupLabel(id),
    users: buckets.get(id)!,
  }));
}
